// Find My Childcare — API client.
//
// Single seam between screens and backend. Today every call resolves from the
// in-memory mock; when the Azure Functions backend lands (Phase 3), flipping
// `window.FMC_CONFIG.useMock = false` switches to the HTTP adapter with zero
// screen changes.

import { BOOKINGS, INQUIRIES, PROVIDERS, PROVIDER_CLASSROOMS, WAITLIST_ENTRIES } from './data';
import type {
  Availability,
  AvailabilityEditor,
  AvailabilityUpdateResult,
  Booking,
  BookingDraft,
  FamilyRequest,
  Inquiry,
  Provider,
  ProviderClassroom,
  ProviderClassroomDraft,
  ProviderNotice,
  ProviderNoticeDraft,
  ProviderPost,
  ProviderPostDraft,
  ProviderProfileUpdate,
  SearchQuery,
  WaitlistEntry,
  WaitlistEntryDraft,
} from './types';

export interface ProvidersApi {
  search(query?: SearchQuery): Promise<Provider[]>;
  get(id: string): Promise<Provider>;
  updateProfile(id: string, profile: ProviderProfileUpdate): Promise<Provider>;
  updateAvailability(
    id: string,
    ages: Partial<AvailabilityEditor>,
  ): Promise<AvailabilityUpdateResult>;
}

export interface InquiriesApi {
  list(providerId: string): Promise<Inquiry[]>;
  confirm(providerId: string, inquiryId: number): Promise<Inquiry>;
}

export interface BookingsApi {
  list(providerId: string): Promise<Booking[]>;
  create(booking: BookingDraft): Promise<Booking>;
  confirm(providerId: string, bookingId: string): Promise<Booking>;
}

export interface PostsApi {
  list(providerId: string): Promise<ProviderPost[]>;
  create(post: ProviderPostDraft): Promise<ProviderPost>;
}

export interface WaitlistsApi {
  list(providerId: string): Promise<WaitlistEntry[]>;
  create(entry: WaitlistEntryDraft): Promise<WaitlistEntry>;
  contact(providerId: string, entryId: string): Promise<WaitlistEntry>;
  place(providerId: string, entryId: string): Promise<WaitlistEntry>;
}

export interface NoticesApi {
  list(providerId: string): Promise<ProviderNotice[]>;
  create(notice: ProviderNoticeDraft): Promise<ProviderNotice>;
}

export interface FamilyRequestsApi {
  lookup(phone: string): Promise<FamilyRequest[]>;
}

export interface ClassroomsApi {
  list(providerId: string): Promise<ProviderClassroom[]>;
  upsert(providerId: string, classroom: ProviderClassroomDraft): Promise<ProviderClassroom>;
}

export interface Api {
  providers: ProvidersApi;
  inquiries: InquiriesApi;
  bookings: BookingsApi;
  posts: PostsApi;
  waitlists: WaitlistsApi;
  notices: NoticesApi;
  classrooms: ClassroomsApi;
  familyRequests: FamilyRequestsApi;
  __mode: 'mock' | 'http';
  __baseUrl: string;
}

// --- shared helpers ---------------------------------------------------

const wait = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
const normalizePhone = (value: string | undefined): string => (value || '').replace(/\D/g, '');

// Map a parent search query to the underlying provider records. Used by both
// the mock adapter and (later) the URL-builder for HTTP.
export const matchProvider = (
  p: Provider,
  { childrenAges, schedule, subsidyOnly }: SearchQuery,
): boolean => {
  if (subsidyOnly && !p.subsidy) return false;
  if (Array.isArray(childrenAges) && childrenAges.length > 0) {
    if (!childrenAges.every((a) => p.ages.includes(a))) return false;
  }
  if (schedule && p.schedules && !p.schedules.includes(schedule)) return false;
  // ZIP is informational in the prototype; real backend will use PostGIS radius.
  return true;
};

export const hasOpeningsForAges = (
  provider: Provider,
  childrenAges: SearchQuery['childrenAges'] = [],
): boolean => {
  if (!childrenAges || childrenAges.length === 0) return provider.status === 'open';
  return childrenAges.every((age) => provider.availability[age].spots > 0);
};

// --- mock adapter -----------------------------------------------------

export interface MockAdapterOptions {
  latencyMs?: number;
  jitterMs?: number;
  seedProviders?: Provider[];
  seedInquiries?: Inquiry[];
  seedBookings?: Booking[];
  seedPosts?: ProviderPost[];
  seedWaitlistEntries?: WaitlistEntry[];
  seedNotices?: ProviderNotice[];
  seedClassrooms?: ProviderClassroom[];
  /** When false, mock methods resolve synchronously — useful in tests. */
  artificialLatency?: boolean;
}

export const createMockAdapter = (opts: MockAdapterOptions = {}): Api => {
  const latency = opts.latencyMs ?? 220;
  const jitter = opts.jitterMs ?? 120;
  const useLatency = opts.artificialLatency !== false;
  const mockDelay = () => (useLatency ? wait(latency + Math.random() * jitter) : Promise.resolve());

  const providers: Provider[] = (opts.seedProviders ?? PROVIDERS).map(clone);
  const inquiries: Inquiry[] = (opts.seedInquiries ?? INQUIRIES).map(clone);
  const bookings: Booking[] = (opts.seedBookings ?? BOOKINGS).map(clone);
  const posts: ProviderPost[] = (opts.seedPosts ?? []).map(clone);
  const waitlistEntries: WaitlistEntry[] = (opts.seedWaitlistEntries ?? WAITLIST_ENTRIES).map(
    clone,
  );
  const notices: ProviderNotice[] = (opts.seedNotices ?? []).map(clone);
  const classrooms: ProviderClassroom[] = (opts.seedClassrooms ?? PROVIDER_CLASSROOMS).map(clone);

  const syncProviderAvailabilityFromClassrooms = (
    provider: Provider,
    ageGroup: ProviderClassroom['ageGroup'],
  ) => {
    const ageClassrooms = classrooms.filter(
      (classroom) => classroom.providerId === provider.id && classroom.ageGroup === ageGroup,
    );
    const openings = ageClassrooms.reduce((sum, classroom) => sum + classroom.openings, 0);
    const waitlistCount = ageClassrooms.reduce(
      (sum, classroom) => sum + classroom.waitlistCount,
      0,
    );
    provider.availability[ageGroup] = {
      spots: openings,
      note:
        openings > 0
          ? 'Confirmed from classroom setup'
          : waitlistCount > 0
            ? `Waitlist · ${waitlistCount} ahead`
            : 'No current openings',
    };
    if (!provider.ages.includes(ageGroup)) provider.ages.push(ageGroup);
    provider.spots = Object.values(provider.availability).reduce(
      (sum, availability) => sum + availability.spots,
      0,
    );
    provider.status = provider.spots > 0 ? 'open' : 'waitlist';
    provider.availabilityUpdatedAt = new Date().toISOString();
  };

  return {
    providers: {
      async search(query = {}) {
        await mockDelay();
        return providers.filter((p) => matchProvider(p, query)).map(clone);
      },
      async get(id) {
        await mockDelay();
        const p = providers.find((x) => x.id === id);
        if (!p) throw new Error(`Provider ${id} not found`);
        return clone(p);
      },
      async updateProfile(id, profile) {
        await mockDelay();
        const provider = providers.find((item) => item.id === id);
        if (!provider) throw new Error(`Provider ${id} not found`);
        provider.description = profile.description;
        provider.hours = profile.hours;
        provider.languages = profile.languages;
        return clone(provider);
      },
      async updateAvailability(id, ages) {
        await mockDelay();
        const p = providers.find((x) => x.id === id);
        if (!p) throw new Error(`Provider ${id} not found`);
        // Clone first so any age groups the caller omits keep their existing values
        // instead of being silently wiped out.
        const next: Availability = { ...p.availability };
        for (const [group, info] of Object.entries(ages) as [
          keyof Availability,
          AvailabilityEditor[keyof AvailabilityEditor],
        ][]) {
          next[group] = {
            spots: info.on ? info.count : 0,
            note: info.note ?? (info.on ? '' : 'Not currently offered'),
          };
        }
        let totalOpen = 0;
        for (const key of Object.keys(next) as (keyof Availability)[]) {
          totalOpen += next[key].spots;
        }
        p.availability = next;
        p.spots = totalOpen;
        p.status = totalOpen > 0 ? 'open' : 'waitlist';
        p.availabilityUpdatedAt = new Date().toISOString();
        return {
          availability: clone(p.availability),
          spots: p.spots,
          status: p.status,
          lastUpdated: p.availabilityUpdatedAt,
        };
      },
    },
    inquiries: {
      async list(_providerId) {
        await mockDelay();
        // Prototype has a single provider's inquiry feed; ignore providerId.
        return inquiries.map(clone);
      },
      async confirm(_providerId, inquiryId) {
        await mockDelay();
        const inq = inquiries.find((i) => i.id === inquiryId);
        if (!inq) throw new Error(`Inquiry ${inquiryId} not found`);
        inq.status = 'confirmed';
        return clone(inq);
      },
    },
    bookings: {
      async list(providerId) {
        await mockDelay();
        return bookings
          .filter((booking) => booking.providerId === providerId)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
          .map(clone);
      },
      async create(booking) {
        await mockDelay();
        const row: Booking = {
          ...booking,
          id: `b_${Date.now()}_${bookings.length}`,
          createdAt: new Date().toISOString(),
          status: 'pending',
        };
        bookings.push(row);
        return clone(row);
      },
      async confirm(providerId, bookingId) {
        await mockDelay();
        const booking = bookings.find(
          (item) => item.providerId === providerId && item.id === bookingId,
        );
        if (!booking) throw new Error(`Booking ${bookingId} not found`);
        booking.status = 'confirmed';
        return clone(booking);
      },
    },
    posts: {
      async list(providerId) {
        await mockDelay();
        return posts
          .filter((post) => post.providerId === providerId)
          .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))
          .map(clone);
      },
      async create(post) {
        await mockDelay();
        const provider = providers.find((item) => item.id === post.providerId);
        if (!provider) throw new Error(`Provider ${post.providerId} not found`);
        const row: ProviderPost = {
          ...post,
          id: `post_${Date.now()}_${posts.length}`,
          publishedAt: new Date().toISOString(),
        };
        posts.push(row);
        return clone(row);
      },
    },
    waitlists: {
      async list(providerId) {
        await mockDelay();
        return waitlistEntries
          .filter((entry) => entry.providerId === providerId)
          .sort((a, b) => a.joinedAt.localeCompare(b.joinedAt))
          .map(clone);
      },
      async create(entry) {
        await mockDelay();
        const provider = providers.find((item) => item.id === entry.providerId);
        if (!provider) throw new Error(`Provider ${entry.providerId} not found`);
        const row: WaitlistEntry = {
          ...entry,
          id: `wait_${Date.now()}_${waitlistEntries.length}`,
          joinedAt: new Date().toISOString(),
          status: 'waiting',
        };
        waitlistEntries.push(row);
        return clone(row);
      },
      async contact(providerId, entryId) {
        await mockDelay();
        const entry = waitlistEntries.find(
          (item) => item.providerId === providerId && item.id === entryId,
        );
        if (!entry) throw new Error(`Waitlist entry ${entryId} not found`);
        entry.status = 'contacted';
        entry.contactedAt = new Date().toISOString();
        return clone(entry);
      },
      async place(providerId, entryId) {
        await mockDelay();
        const entry = waitlistEntries.find(
          (item) => item.providerId === providerId && item.id === entryId,
        );
        if (!entry) throw new Error(`Waitlist entry ${entryId} not found`);
        if (entry.status !== 'contacted') {
          throw new Error(`Waitlist entry ${entryId} must be contacted before placement`);
        }
        entry.status = 'placed';
        return clone(entry);
      },
    },
    notices: {
      async list(providerId) {
        await mockDelay();
        return notices
          .filter((notice) => notice.providerId === providerId)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
          .map(clone);
      },
      async create(notice) {
        await mockDelay();
        const provider = providers.find((item) => item.id === notice.providerId);
        if (!provider) throw new Error(`Provider ${notice.providerId} not found`);
        const row: ProviderNotice = {
          ...notice,
          id: `notice_${Date.now()}_${notices.length}`,
          createdAt: new Date().toISOString(),
        };
        notices.push(row);
        return clone(row);
      },
    },
    classrooms: {
      async list(providerId) {
        await mockDelay();
        return classrooms
          .filter((classroom) => classroom.providerId === providerId)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(clone);
      },
      async upsert(providerId, draft) {
        await mockDelay();
        const provider = providers.find((item) => item.id === providerId);
        if (!provider) throw new Error(`Provider ${providerId} not found`);
        const name = draft.name.trim();
        if (!name) throw new Error('Classroom name is required');
        if (!Number.isInteger(draft.capacity) || draft.capacity < 1) {
          throw new Error('Classroom capacity must be a positive whole number');
        }
        if (!Number.isInteger(draft.enrolled) || draft.enrolled < 0) {
          throw new Error('Enrollment must be a whole number');
        }
        if (draft.enrolled > draft.capacity) {
          throw new Error('Enrollment cannot exceed classroom capacity');
        }
        if (!Number.isInteger(draft.waitlistCount) || draft.waitlistCount < 0) {
          throw new Error('Waitlist count must be a whole number');
        }

        const existing = draft.id
          ? classrooms.find(
              (classroom) => classroom.providerId === providerId && classroom.id === draft.id,
            )
          : undefined;
        if (draft.id && !existing) throw new Error(`Classroom ${draft.id} not found`);

        const row: ProviderClassroom = {
          id: existing?.id ?? `room_${Date.now()}_${classrooms.length}`,
          providerId,
          name,
          ageGroup: draft.ageGroup,
          capacity: draft.capacity,
          enrolled: draft.enrolled,
          openings: draft.capacity - draft.enrolled,
          schedule: draft.schedule,
          openingDate: draft.openingDate,
          waitlistCount: draft.waitlistCount,
        };
        const previousAgeGroup = existing?.ageGroup;
        if (existing) Object.assign(existing, row);
        else classrooms.push(row);
        if (previousAgeGroup && previousAgeGroup !== row.ageGroup) {
          syncProviderAvailabilityFromClassrooms(provider, previousAgeGroup);
        }
        syncProviderAvailabilityFromClassrooms(provider, row.ageGroup);
        return clone(row);
      },
    },
    familyRequests: {
      async lookup(phone) {
        await mockDelay();
        const normalized = normalizePhone(phone);
        if (normalized.length !== 10) return [];

        const tourRequests: FamilyRequest[] = bookings
          .filter(
            (booking): booking is Booking & { phone: string } =>
              typeof booking.phone === 'string' && normalizePhone(booking.phone) === normalized,
          )
          .map((booking) => ({
            id: booking.id,
            kind: 'tour',
            providerId: booking.providerId,
            providerName:
              providers.find((provider) => provider.id === booking.providerId)?.name ||
              'Child care provider',
            ageGroup: booking.childAge,
            phone: booking.phone,
            status: booking.status,
            createdAt: booking.createdAt,
            date: booking.date,
            time: booking.time,
            tourType: booking.tourType,
          }));

        const waitlistRequests: FamilyRequest[] = waitlistEntries
          .filter((entry) => normalizePhone(entry.phone) === normalized)
          .map((entry) => ({
            id: entry.id,
            kind: 'waitlist',
            providerId: entry.providerId,
            providerName:
              providers.find((provider) => provider.id === entry.providerId)?.name ||
              'Child care provider',
            ageGroup: entry.childAge,
            phone: entry.phone,
            status: entry.status,
            createdAt: entry.joinedAt,
          }));

        return [...tourRequests, ...waitlistRequests]
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
          .map(clone);
      },
    },
    __mode: 'mock',
    __baseUrl: '',
  };
};

// --- http adapter -----------------------------------------------------

const qs = (params?: Record<string, unknown>): string => {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue;
    if (Array.isArray(v)) {
      for (const item of v) usp.append(k, String(item));
    } else {
      usp.set(k, String(v));
    }
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
};

export const createHttpAdapter = (baseUrl: string): Api => {
  const base = baseUrl.replace(/\/$/, '');
  const request = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
    const res = await fetch(`${base}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`${method} ${path} -> ${res.status} ${text}`);
    }
    if (res.status === 204) return null as T;
    return (await res.json()) as T;
  };

  return {
    providers: {
      search: (q) => request('GET', `/providers${qs(q as Record<string, unknown>)}`),
      get: (id) => request('GET', `/providers/${encodeURIComponent(id)}`),
      updateProfile: (id, profile) =>
        request('POST', `/providers/${encodeURIComponent(id)}/profile`, profile),
      updateAvailability: (id, ages) =>
        request('POST', `/providers/${encodeURIComponent(id)}/availability`, { ages }),
    },
    inquiries: {
      list: (providerId) =>
        request('GET', `/providers/${encodeURIComponent(providerId)}/inquiries`),
      confirm: (providerId, inquiryId) =>
        request(
          'POST',
          `/providers/${encodeURIComponent(providerId)}/inquiries/${encodeURIComponent(String(inquiryId))}/confirm`,
        ),
    },
    bookings: {
      list: (providerId) => request('GET', `/providers/${encodeURIComponent(providerId)}/bookings`),
      create: (booking) => request('POST', '/bookings', booking),
      confirm: (providerId, bookingId) =>
        request(
          'POST',
          `/providers/${encodeURIComponent(providerId)}/bookings/${encodeURIComponent(bookingId)}/confirm`,
        ),
    },
    posts: {
      list: (providerId) => request('GET', `/providers/${encodeURIComponent(providerId)}/posts`),
      create: (post) =>
        request('POST', `/providers/${encodeURIComponent(post.providerId)}/posts`, post),
    },
    waitlists: {
      list: (providerId) => request('GET', `/providers/${encodeURIComponent(providerId)}/waitlist`),
      create: (entry) =>
        request('POST', `/providers/${encodeURIComponent(entry.providerId)}/waitlist`, entry),
      contact: (providerId, entryId) =>
        request(
          'POST',
          `/providers/${encodeURIComponent(providerId)}/waitlist/${encodeURIComponent(entryId)}/contact`,
        ),
      place: (providerId, entryId) =>
        request(
          'POST',
          `/providers/${encodeURIComponent(providerId)}/waitlist/${encodeURIComponent(entryId)}/place`,
        ),
    },
    notices: {
      list: (providerId) => request('GET', `/providers/${encodeURIComponent(providerId)}/notices`),
      create: (notice) =>
        request('POST', `/providers/${encodeURIComponent(notice.providerId)}/notices`, notice),
    },
    classrooms: {
      list: (providerId) =>
        request('GET', `/providers/${encodeURIComponent(providerId)}/classrooms`),
      upsert: (providerId, classroom) =>
        request('POST', `/providers/${encodeURIComponent(providerId)}/classrooms`, classroom),
    },
    familyRequests: {
      lookup: (phone) => request('GET', `/family/requests${qs({ phone })}`),
    },
    __mode: 'http',
    __baseUrl: base,
  };
};

// --- default singleton ------------------------------------------------

const cfg = typeof window !== 'undefined' ? (window.FMC_CONFIG ?? {}) : {};
const USE_MOCK = cfg.useMock !== false;
const BASE_URL = (cfg.apiBaseUrl ?? '/api').replace(/\/$/, '');

export const api: Api = USE_MOCK
  ? createMockAdapter({ latencyMs: cfg.artificialLatencyMs ?? 220 })
  : createHttpAdapter(BASE_URL);
